<div class="w-full h-full flex flex-col p-4 dark:text-app-gray-300">
  <div class="flex items-start justify-between mb-1 gap-4">
    <div class="flex items-center gap-3">
      <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Error Reports</h2>
      <span class="text-[11px] text-gray-500 dark:text-gray-400">Records auto-expire after 30 days.</span>
    </div>
    <button type="button"
            class="text-[11px] uppercase tracking-wide border border-gray-300 dark:border-[#434863] rounded px-2 py-1
                   text-gray-700 dark:text-gray-300 hover:text-app-teal-100 hover:border-app-teal-100 transition"
            (click)="loadList()">
      <i class="fa-solid fa-rotate mr-1"></i>Refresh
    </button>
  </div>
  <p class="text-xs text-gray-500 dark:text-gray-400 mb-4">
    Submitted by consumer SPAs when error toasts fire. Paste a support ID from a user's toast into the lookup field to load the full diagnostics.
  </p>

  <!-- ============================================================
       LOOKUP-BY-ID — primary use case: support pasted from a toast.
       Tolerates pasted prefixes / surrounding text (e.g. the operator
       pastes "Support ID: ER-7M4QPK3" verbatim from chat).
       ============================================================ -->
  <div class="border border-gray-200 dark:border-[#434863] rounded p-3 mb-4 bg-gray-50 dark:bg-app-black-100/40">
    <label class="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
      Lookup by support ID
    </label>
    <div class="flex gap-2">
      <input type="text" [(ngModel)]="lookupId" (keyup.enter)="onLookup()"
             placeholder="Paste an ID (or the whole 'Support ID: ER-XXXXXXX' line)"
             class="flex-1 bg-white border border-gray-300 text-gray-900 rounded px-3 py-1.5 text-sm font-mono dark:bg-app-black-100 dark:border-[#434863] dark:text-white" />
      <button type="button" class="dark-btn"
              [class.dark-btn-disabled]="loadingLookup"
              [disabled]="loadingLookup"
              (click)="onLookup()">
        <i class="fa-solid fa-magnifying-glass mr-1"></i>
        <ng-container *ngIf="!loadingLookup">Lookup</ng-container>
        <ng-container *ngIf="loadingLookup">Loading…</ng-container>
      </button>
      <button *ngIf="selected || lookupId" type="button" class="dark-btn"
              (click)="clearSelection()">
        <i class="fa-solid fa-xmark mr-1"></i>Clear
      </button>
    </div>
    <p *ngIf="lookupErr" class="mt-2 text-xs text-red-400">{{ lookupErr }}</p>
  </div>

  <!-- ============================================================
       DETAIL VIEW — set by either the lookup OR a click on the table.
       ============================================================ -->
  <div *ngIf="selected" class="border border-app-teal-100 dark:border-app-teal-100/60 rounded mb-6 bg-white dark:bg-app-black-500">
    <div class="flex items-center justify-between px-3 py-2 bg-app-teal-100/10 border-b border-app-teal-100/30">
      <div class="flex items-center gap-3">
        <code class="text-sm font-mono font-semibold text-app-teal-100">{{ selected._id }}</code>
        <button type="button" class="text-gray-500 dark:text-gray-400 hover:text-app-teal-100"
                (click)="copyText(selected._id)" title="Copy support ID">
          <i class="fa-regular fa-copy"></i>
        </button>
        <span class="text-xs text-gray-500 dark:text-gray-400">{{ formatRelative(selected.created_at) }}</span>
        <span *ngIf="selected.error_code" class="text-[11px] uppercase tracking-wide px-2 py-0.5 rounded bg-red-900/30 border border-red-500/40 text-red-300">{{ selected.error_code }}</span>
      </div>
      <div class="flex items-center gap-3">
        <button type="button" class="text-xs text-gray-500 dark:text-gray-400 hover:text-app-teal-100"
                (click)="copyText(prettyJson(selected))" title="Copy the full report as JSON">
          <i class="fa-regular fa-copy mr-1"></i>copy JSON
        </button>
        <button type="button" class="text-xs text-gray-500 dark:text-gray-400 hover:text-app-teal-100"
                (click)="copyLink(selected._id)"
                [title]="'Copy deep-link to this report: ' + linkFor(selected._id)">
          <i class="fa-solid fa-link mr-1"></i>copy link
        </button>
      </div>
    </div>
    <div class="p-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-xs">
      <div><span class="font-semibold text-gray-700 dark:text-gray-300">When (ET):</span> <code class="text-app-teal-100">{{ formatDetailWhen(selected.created_at) }}</code></div>
      <div>
        <span class="font-semibold text-gray-700 dark:text-gray-300">User:</span>
        <ng-container *ngIf="detailUserEmail(selected) as ue; else noUser">
          <a [routerLink]="['/admin/users']" [queryParams]="{ email: ue }"
             class="text-app-teal-100 hover:underline"
             title="Open the Users panel filtered to this email">
            <code>{{ ue }}</code>
            <i class="fa-solid fa-up-right-from-square ml-1 text-[10px] text-gray-500 dark:text-gray-400"></i>
          </a>
        </ng-container>
        <ng-template #noUser><code class="text-app-teal-100">—</code></ng-template>
      </div>
      <div><span class="font-semibold text-gray-700 dark:text-gray-300">Customer:</span> <code class="text-app-teal-100">{{ selected.customer || '—' }}</code></div>
      <div><span class="font-semibold text-gray-700 dark:text-gray-300">Path:</span> <code class="text-app-teal-100">{{ selected.scanner_path || '—' }}</code></div>
      <div class="md:col-span-2 break-all">
        <span class="font-semibold text-gray-700 dark:text-gray-300">URL:</span>
        <code class="text-app-teal-100">{{ selected.url || '—' }}</code>
        <ng-container *ngIf="selected.url">
          <button type="button"
                  class="ml-2 text-gray-500 dark:text-gray-400 hover:text-app-teal-100"
                  (click)="copyText(selected.url)"
                  title="Copy URL">
            <i class="fa-regular fa-copy"></i>
          </button>
          <a [href]="selected.url" target="_blank" rel="noopener noreferrer"
             class="ml-1 text-gray-500 dark:text-gray-400 hover:text-app-teal-100"
             title="Open URL in a new tab">
            <i class="fa-solid fa-arrow-up-right-from-square"></i>
          </a>
        </ng-container>
      </div>
      <div><span class="font-semibold text-gray-700 dark:text-gray-300">HTTP status:</span> <code class="text-app-teal-100">{{ selected.http_status !== null && selected.http_status !== undefined ? selected.http_status : '—' }}</code></div>
      <div><span class="font-semibold text-gray-700 dark:text-gray-300">User IP:</span> <code class="text-app-teal-100">{{ selected.user_ip || '—' }}</code></div>
      <div *ngIf="selected.x_forwarded_for" class="md:col-span-2 break-all"><span class="font-semibold text-gray-700 dark:text-gray-300">X-Forwarded-For:</span> <code class="text-app-teal-100">{{ selected.x_forwarded_for }}</code></div>
      <div class="md:col-span-2"><span class="font-semibold text-gray-700 dark:text-gray-300">F2 response message:</span> <code class="text-app-teal-100">{{ selected.f2_response_message || '—' }}</code></div>
      <div *ngIf="selected.f2_response_body" class="md:col-span-2">
        <div class="flex items-center justify-between mb-1">
          <div class="font-semibold text-gray-700 dark:text-gray-300">F2 response body</div>
          <button type="button"
                  class="text-[10px] uppercase tracking-wide px-2 py-1 border border-gray-300 dark:border-[#434863] rounded"
                  (click)="copyText(selected.f2_response_body); flashCopy('resp')">
            <i class="fa-solid" [ngClass]="copied === 'resp' ? 'fa-check text-emerald-500' : 'fa-copy'"></i>
            <span class="ml-1">{{ copied === 'resp' ? 'Copied' : 'Copy' }}</span>
          </button>
        </div>
        <pre class="bg-gray-50 dark:bg-app-black-100 border border-gray-300 dark:border-[#434863] rounded p-2 font-mono leading-snug text-gray-800 dark:text-gray-200 overflow-x-auto max-h-64">{{ selected.f2_response_body }}</pre>
      </div>
      <div *ngIf="selected.user_agent" class="md:col-span-2 break-all"><span class="font-semibold text-gray-700 dark:text-gray-300">User agent:</span> <span class="text-gray-600 dark:text-gray-400">{{ selected.user_agent }}</span></div>
      <!-- WooCommerce on-demand fetch: renders when the ER carries a
           wc_subscription_lookup_url extra (all WC errors emitted
           2026-07-09+). Pulls the CURRENT WC sub state via our
           /commerce/woocommerce/subscription/:id proxy so ops sees the
           latest payload without us storing it in mongo. -->
      <div *ngIf="selected.extra?.['wc_subscription_lookup_url']" class="md:col-span-2">
        <div class="flex items-center justify-between mb-1">
          <div class="font-semibold text-gray-700 dark:text-gray-300">
            WooCommerce subscription
            <span class="ml-2 normal-case text-[10px] text-gray-400">
              id {{ selected.extra?.['wc_subscription_id'] || '—' }}
            </span>
          </div>
          <div class="flex items-center gap-2">
            <button type="button"
                    class="text-[10px] uppercase tracking-wide px-2 py-1 border border-emerald-500 text-emerald-600 dark:text-emerald-400 rounded hover:bg-emerald-50 dark:hover:bg-emerald-500/10"
                    [disabled]="wcFetching"
                    (click)="fetchWcSubscription(selected)">
              <i class="fa-solid" [ngClass]="wcFetching ? 'fa-circle-notch fa-spin' : 'fa-cloud-arrow-down'"></i>
              <span class="ml-1">{{ wcFetching ? 'Fetching…' : 'Fetch current WC state' }}</span>
            </button>
            <button *ngIf="wcFetchResult"
                    type="button"
                    class="text-[10px] uppercase tracking-wide px-2 py-1 border border-gray-300 dark:border-[#434863] rounded"
                    (click)="copyText(prettyJson(wcFetchResult)); flashCopy('wc')">
              <i class="fa-solid" [ngClass]="copied === 'wc' ? 'fa-check text-emerald-500' : 'fa-copy'"></i>
              <span class="ml-1">{{ copied === 'wc' ? 'Copied' : 'Copy' }}</span>
            </button>
          </div>
        </div>
        <div *ngIf="wcFetchError" class="text-[11px] text-red-600 dark:text-red-400 mb-1">
          <i class="fa-solid fa-triangle-exclamation mr-1"></i>{{ wcFetchError }}
        </div>
        <pre *ngIf="wcFetchResult"
             class="bg-gray-50 dark:bg-app-black-100 border border-gray-300 dark:border-[#434863] rounded p-2 font-mono leading-snug text-gray-800 dark:text-gray-200 overflow-x-auto max-h-64">{{ prettyJson(wcFetchResult) }}</pre>
        <div *ngIf="!wcFetchResult && !wcFetching && !wcFetchError" class="text-[11px] italic text-gray-500 dark:text-gray-400">
          Click Fetch to retrieve the current subscription state from {{ selected.customer || 'WooCommerce' }} on demand.
        </div>
      </div>

      <div *ngIf="selected.extra && selected.extra | keyvalue : null as kv" class="md:col-span-2">
        <div class="flex items-center justify-between mb-1">
          <div class="font-semibold text-gray-700 dark:text-gray-300">Extra context</div>
          <button type="button"
                  class="text-[10px] uppercase tracking-wide px-2 py-1 border border-gray-300 dark:border-[#434863] rounded"
                  (click)="copyText(prettyJson(selected.extra)); flashCopy('extra')">
            <i class="fa-solid" [ngClass]="copied === 'extra' ? 'fa-check text-emerald-500' : 'fa-copy'"></i>
            <span class="ml-1">{{ copied === 'extra' ? 'Copied' : 'Copy' }}</span>
          </button>
        </div>
        <pre class="bg-gray-50 dark:bg-app-black-100 border border-gray-300 dark:border-[#434863] rounded p-2 font-mono leading-snug text-gray-800 dark:text-gray-200 overflow-x-auto max-h-64">{{ prettyJson(selected.extra) }}</pre>
      </div>
    </div>
  </div>

  <!-- ============================================================
       LIST — recent error reports. ag-grid floating filters narrow
       per-column; status bar shows total + filtered counts. Row click
       loads detail above. Permalink: /admin/error-reports/<id>.
       ============================================================ -->
  <div class="border border-gray-200 dark:border-[#434863] rounded flex flex-col flex-1 min-h-0">
    <div class="px-3 py-2 bg-gray-100 dark:bg-app-black-100 border-b border-gray-200 dark:border-[#434863] flex items-center gap-3 flex-wrap flex-none">
      <span class="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Recent reports</span>
      <div class="ml-auto flex items-center gap-2">
        <!-- Live toggle. When active (and not suspended by paging), an
             EventSource against /admin/error-reports/stream prepends
             new reports as they're inserted. The pulsing dot is the
             at-a-glance indicator that realtime is on the wire. -->
        <button type="button"
                class="text-[10px] uppercase tracking-wide border rounded px-2 py-0.5 transition inline-flex items-center gap-1.5"
                [class.text-app-teal-100]="liveOn && !liveSuspended"
                [class.border-app-teal-100]="liveOn && !liveSuspended"
                [class.text-gray-500]="!(liveOn && !liveSuspended)"
                [class.dark:text-gray-400]="!(liveOn && !liveSuspended)"
                [class.border-gray-300]="!(liveOn && !liveSuspended)"
                [class.dark:border-[#434863]]="!(liveOn && !liveSuspended)"
                (click)="toggleLive()"
                [title]="liveOn ? 'Live updates are ON — new reports stream in at the top' : 'Turn on live updates — new reports will stream in at the top'">
          <span class="live-dot" [class.live-dot--on]="liveOn && !liveSuspended"></span>
          Live
        </button>
        <!-- Sound mute toggle + volume slider. Both persisted per-operator
             in localStorage (independent of per-customer config). The
             customer-configured instrument + note still determine WHAT
             plays; mute/volume control whether and how loud. -->
        <div class="inline-flex items-center gap-1 border border-gray-300 dark:border-[#434863] rounded px-2 py-0.5">
          <button type="button"
                  class="text-[10px] uppercase tracking-wide"
                  [class.text-gray-500]="soundPlayer.isMuted()"
                  [class.dark:text-gray-400]="soundPlayer.isMuted()"
                  [class.text-app-teal-100]="!soundPlayer.isMuted()"
                  [class.dark:text-app-teal-100]="!soundPlayer.isMuted()"
                  (click)="toggleSoundMute()"
                  [title]="soundPlayer.isMuted() ? 'Sound is muted — click to enable alert tones' : 'Sound is on — click to mute alert tones'">
            <i class="fa-solid mr-1" [class.fa-volume-xmark]="soundPlayer.isMuted()" [class.fa-volume-high]="!soundPlayer.isMuted()"></i>
            {{ soundPlayer.isMuted() ? 'Muted' : 'Sound' }}
          </button>
          <input type="range" min="0" max="100" step="5"
                 [value]="soundPlayer.getVolume()"
                 (input)="onVolumeChange($any($event.target).value)"
                 class="w-20 align-middle accent-app-teal-100"
                 [class.opacity-50]="soundPlayer.isMuted()"
                 [disabled]="soundPlayer.isMuted()"
                 [title]="'Volume: ' + soundPlayer.getVolume() + '%'" />
          <span class="text-[9px] tabular-nums text-gray-500 dark:text-gray-400 w-7 text-right">
            {{ soundPlayer.getVolume() }}%
          </span>
          <button type="button"
                  (click)="openSoundSettings()"
                  class="ml-1 text-gray-500 dark:text-gray-400 hover:text-app-teal-100"
                  title="Configure per-customer alert sounds">
            <i class="fa-solid fa-gear text-[11px]"></i>
          </button>
        </div>
        <button type="button"
                class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 hover:text-app-teal-100 border border-gray-300 dark:border-[#434863] rounded px-2 py-0.5 disabled:opacity-40 disabled:cursor-not-allowed"
                (click)="pageNewer()"
                [disabled]="pageStack.length === 0 || loadingList"
                title="Newer page (back toward the most recent)">
          <i class="fa-solid fa-arrow-up mr-1"></i>Newer
        </button>
        <button type="button"
                class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 hover:text-app-teal-100 border border-gray-300 dark:border-[#434863] rounded px-2 py-0.5 disabled:opacity-40 disabled:cursor-not-allowed"
                (click)="pageOlder()"
                [disabled]="!hasOlder || loadingList"
                title="Older page (deeper into history; suspends live updates)">
          <i class="fa-solid fa-arrow-down mr-1"></i>Older
        </button>
        <button type="button"
                class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 hover:text-app-teal-100 border border-gray-300 dark:border-[#434863] rounded px-2 py-0.5"
                (click)="loadList()" title="Re-fetch the latest 100 reports">
          <i class="fa-solid fa-rotate mr-1"></i>Refresh
        </button>
        <!-- Clear Filters: reset every server-side + ag-grid filter to
             defaults, reload page 1. Turns red when any filter is active
             so operators can see 'the list is narrow' at a glance. Mike
             2026-07-02. -->
        <button type="button"
                class="text-[10px] uppercase tracking-wide border rounded px-2 py-0.5"
                [class.text-red-500]="hasActiveFilters()"
                [class.dark:text-red-400]="hasActiveFilters()"
                [class.border-red-500]="hasActiveFilters()"
                [class.dark:border-red-400]="hasActiveFilters()"
                [class.hover:bg-red-500]="hasActiveFilters()"
                [class.hover:text-white]="hasActiveFilters()"
                [class.text-gray-500]="!hasActiveFilters()"
                [class.dark:text-gray-400]="!hasActiveFilters()"
                [class.border-gray-300]="!hasActiveFilters()"
                [class.dark:border-[#434863]]="!hasActiveFilters()"
                [class.hover:text-app-teal-100]="!hasActiveFilters()"
                (click)="clearAllFilters()"
                [title]="hasActiveFilters() ? 'Clear all filters — filters are currently active' : 'No filters currently active'">
          <i class="fa-solid fa-filter-circle-xmark mr-1"></i>Clear filters
        </button>
        <button type="button"
                class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 hover:text-app-teal-100 border border-gray-300 dark:border-[#434863] rounded px-2 py-0.5"
                (click)="onCopyVisibleRowsIds()"
                [title]="selectedCount > 0 ? 'Copy support IDs of the ' + selectedCount + ' selected report(s)' : 'Copy support IDs of every visible report'">
          <i class="fa-regular fa-copy mr-1"></i>{{ copyButtonLabel('Copy IDs') }}
        </button>
        <button type="button"
                class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 hover:text-app-teal-100 border border-gray-300 dark:border-[#434863] rounded px-2 py-0.5"
                (click)="onCopyVisibleRowsJson()"
                [title]="selectedCount > 0 ? 'Copy the ' + selectedCount + ' selected report(s) as JSON' : 'Copy the visible rows (after sort + filter) as JSON — full records, every field'">
          <i class="fa-regular fa-copy mr-1"></i>{{ copyButtonLabel('Copy JSON') }}
        </button>
        <button type="button"
                class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 hover:text-app-teal-100 border border-gray-300 dark:border-[#434863] rounded px-2 py-0.5"
                (click)="onCopyVisibleRowsCsv()"
                [title]="selectedCount > 0 ? 'Copy the ' + selectedCount + ' selected report(s) as CSV' : 'Copy the visible rows (after sort + filter) as CSV — the visible grid columns only'">
          <i class="fa-regular fa-copy mr-1"></i>{{ copyButtonLabel('Copy CSV') }}
        </button>
        <!-- Admin-only bulk delete. Visible at all times so an operator
             knows the action exists, disabled until rows are selected so
             you can't fire it without a selection. 30-day TTL still prunes
             anything an operator misses. -->
        <button type="button"
                class="text-[10px] uppercase tracking-wide border rounded px-2 py-0.5"
                [class.text-red-500]="selectedCount > 0"
                [class.dark:text-red-400]="selectedCount > 0"
                [class.border-red-500]="selectedCount > 0"
                [class.dark:border-red-400]="selectedCount > 0"
                [class.hover:bg-red-500]="selectedCount > 0"
                [class.hover:text-white]="selectedCount > 0"
                [class.text-gray-400]="selectedCount === 0"
                [class.dark:text-gray-600]="selectedCount === 0"
                [class.border-gray-300]="selectedCount === 0"
                [class.dark:border-[#434863]]="selectedCount === 0"
                [class.cursor-not-allowed]="selectedCount === 0 || deletingSelected"
                [disabled]="selectedCount === 0 || deletingSelected"
                (click)="deleteSelected()"
                [title]="selectedCount > 0 ? 'Permanently delete the ' + selectedCount + ' selected report(s) — cannot be undone' : 'Select one or more rows to enable bulk delete'">
          <i class="fa-solid fa-trash mr-1"></i>
          <ng-container *ngIf="!deletingSelected">{{ selectedCount > 0 ? 'Delete (' + selectedCount + ')' : 'Delete' }}</ng-container>
          <ng-container *ngIf="deletingSelected">Deleting…</ng-container>
        </button>
      </div>
    </div>

    <!-- Live-paused banner: shown when the operator pages off page 1.
         Click to jump back to the newest page and resume the stream. -->
    <div *ngIf="liveOn && liveSuspended"
         class="px-3 py-1.5 text-[11px] bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border-b border-amber-300 dark:border-amber-700/60 flex items-center gap-2 flex-none">
      <i class="fa-solid fa-pause"></i>
      <span>Live paused while you're browsing history.</span>
      <button type="button"
              class="ml-auto text-[10px] uppercase tracking-wide border border-amber-400 dark:border-amber-500 rounded px-2 py-0.5 hover:bg-amber-200 dark:hover:bg-amber-800/40"
              (click)="returnToNewest()">
        Return to newest
      </button>
    </div>

    <!-- Pre-first-load placeholders only — once the grid is mounted
         (hasInitialized) we let ag-grid's own row-count status panel +
         empty grid handle the empty state. Unmounting the grid on
         refilter would wipe the column filter model. -->
    <p *ngIf="!hasInitialized && loadingList" class="p-3 text-xs text-gray-500 dark:text-gray-400">Loading…</p>
    <p *ngIf="listErr" class="p-3 text-xs text-red-400">{{ listErr }}</p>
    <p *ngIf="!hasInitialized && !loadingList && !listErr && list.length === 0" class="p-3 text-xs text-gray-500 dark:text-gray-400 italic">
      No reports in the last 30 days.
    </p>

    <!-- ag-grid: sort, per-column floating filters, total/filtered counts
         in the status bar. Row click loads the detail panel above.
         flex-1 + h-[600px] floor mirrors the users page so the grid
         tracks the browser viewport when resized.
         Mounted once `hasInitialized` flips (first loadList completes,
         success OR failure) and STAYS mounted across re-filters /
         refreshes so the column filter model survives. -->
    <ag-grid-angular *ngIf="hasInitialized"
                     class="flex-1 w-full h-[600px]"
                     [theme]="theme"
                     [gridOptions]="grid.options"
                     [columnDefs]="grid.columnDefs"
                     [rowData]="list"
                     [statusBar]="grid.statusBar">
    </ag-grid-angular>
  </div>
</div>

<!-- Per-customer alert sound editor — opened from the gear icon next to
     the Sound/Volume toolbar group. Auto-saves per change. -->
<sound-settings-dialog *ngIf="showSoundSettings"
                       (close)="closeSoundSettings()">
</sound-settings-dialog>
